import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Dumbbell, Layers, Repeat, Weight, Info, CheckCircle, HelpCircle } from 'lucide-react';

const SharedExercises = () => {
    const { shareToken } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completingExercise, setCompletingExercise] = useState(null);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [showExerciseModal, setShowExerciseModal] = useState(false);

    // Добавляем обработчик глобальных ошибок
    useEffect(() => {
        const handleError = (event) => {
            console.error('Глобальная ошибка:', event.error);
            setError('Произошла ошибка при загрузке страницы');
        };

        const handleUnhandledRejection = (event) => {
            console.error('Необработанная ошибка Promise:', event.reason);
            setError('Произошла ошибка при загрузке страницы');
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    useEffect(() => {
        if (!shareToken) {
            setError('Неверная ссылка');
            setLoading(false);
            return;
        }

        // Проверяем формат токена (UUID)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(shareToken)) {
            setError('Неверный формат ссылки');
            setLoading(false);
            return;
        }

        const fetchSharedExercises = async () => {
            try {
                console.log('Загружаем данные для токена:', shareToken);
                console.log('User-Agent:', navigator.userAgent);
                console.log('Онлайн статус:', navigator.onLine);
                
                // Добавляем обработку ошибок сети
                if (!navigator.onLine) {
                    setError('Нет подключения к интернету');
                    return;
                }
                
                // Добавляем таймаут для мобильных устройств
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд
                
                const response = await fetch(`/api/shared-workout-exercises/${shareToken}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    },
                    signal: controller.signal
                });
                
                clearTimeout(timeoutId);
                
                console.log('Ответ сервера:', response.status, response.statusText);
                
                if (!response.ok) {
                    let errorMessage = 'Ошибка при загрузке данных';
                    
                    if (response.status === 404) {
                        errorMessage = 'Ссылка не найдена';
                    } else if (response.status === 410) {
                        errorMessage = 'Ссылка истекла или деактивирована';
                    } else if (response.status === 401) {
                        errorMessage = 'Ошибка авторизации';
                    } else if (response.status === 403) {
                        errorMessage = 'Доступ запрещен';
                    } else if (response.status >= 500) {
                        errorMessage = 'Ошибка сервера';
                    }
                    
                    setError(errorMessage);
                    return;
                }

                const result = await response.json();
                console.log('Полученные данные:', result);
                setData(result);
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error);
                
                // Более детальная обработка ошибок
                if (error.name === 'AbortError') {
                    setError('Превышено время ожидания. Попробуйте еще раз.');
                } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
                    setError('Ошибка сети. Проверьте подключение к интернету.');
                } else if (error.name === 'SyntaxError') {
                    setError('Ошибка в формате данных');
                } else {
                    setError('Ошибка при загрузке данных');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSharedExercises();
    }, [shareToken]);

    const formatDate = (dateString, timezone = 'Europe/Moscow') => {
        const date = new Date(dateString);
        
        // Форматируем дату в часовом поясе тренера
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone
        });
    };

    const formatTime = (seconds) => {
        if (!seconds) return null;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleShowExerciseDetails = (exercise) => {
        setSelectedExercise(exercise);
        setShowExerciseModal(true);
    };

    const handleCompleteExercise = async (workoutExerciseId) => {
        if (completingExercise !== null) return; // Предотвращаем множественные запросы
        
        console.log('Начинаем выполнение упражнения с ID:', workoutExerciseId);
        console.log('Текущие данные:', data);
        
        // Находим индекс упражнения в массиве для UI
        const exerciseIndex = data.exercises.findIndex(ex => ex.workout_exercise_id === workoutExerciseId);
        console.log('Найденный индекс в массиве:', exerciseIndex);
        
        setCompletingExercise(exerciseIndex);
        try {
            console.log('Отправляем запрос на выполнение упражнения с ID:', workoutExerciseId);
            const response = await fetch(`/api/shared-workout-exercises/${shareToken}/complete-exercise`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ workout_exercise_id: workoutExerciseId })
            });

            console.log('Статус ответа:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Ошибка сервера:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Ответ сервера:', result);
            
            // Перезагружаем данные с сервера для гарантии актуальности
            try {
                const refreshResponse = await fetch(`/api/shared-workout-exercises/${shareToken}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                });
                
                if (refreshResponse.ok) {
                    const refreshedData = await refreshResponse.json();
                    console.log('Обновленные данные с сервера:', refreshedData);
                    setData(refreshedData);
                } else {
                    console.warn('Не удалось обновить данные с сервера, используем локальное обновление');
                    // Fallback к локальному обновлению
                    if (data) {
                const updatedData = { ...data };
                
                // Проверяем, что индекс в пределах массива
                if (exerciseIndex >= 0 && exerciseIndex < updatedData.exercises.length) {
                    updatedData.exercises[exerciseIndex].is_completed = result.is_completed;
                    updatedData.exercises[exerciseIndex].completed_at = result.is_completed ? new Date().toISOString() : null;
                } else {
                    console.error('Неверный индекс упражнения:', exerciseIndex, 'массив:', updatedData.exercises.length);
                }
                
                // Пересчитываем количество выполненных упражнений
                const completedCount = updatedData.exercises.filter(ex => ex.is_completed).length;
                updatedData.completed_exercises = completedCount;
                console.log('Обновленные данные:', updatedData);
                setData(updatedData);
                    }
                }
            } catch (refreshError) {
                console.error('Ошибка при обновлении данных с сервера:', refreshError);
                // Используем локальное обновление как fallback
                if (data) {
                    const updatedData = { ...data };
                    
                    // Проверяем, что индекс в пределах массива
                    if (exerciseIndex >= 0 && exerciseIndex < updatedData.exercises.length) {
                        updatedData.exercises[exerciseIndex].is_completed = result.is_completed;
                        updatedData.exercises[exerciseIndex].completed_at = result.is_completed ? new Date().toISOString() : null;
                    } else {
                        console.error('Неверный индекс упражнения:', exerciseIndex, 'массив:', updatedData.exercises.length);
                    }
                    
                    // Пересчитываем количество выполненных упражнений
                    const completedCount = updatedData.exercises.filter(ex => ex.is_completed).length;
                    updatedData.completed_exercises = completedCount;
                    console.log('Fallback обновленные данные:', updatedData);
                    setData(updatedData);
                }
            }
        } catch (error) {
            console.error('Ошибка при выполнении упражнения:', error);
            alert('Ошибка при выполнении упражнения. Попробуйте еще раз.');
        } finally {
            setCompletingExercise(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка упражнений...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-4">
                    <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Info className="text-red-600" size={24} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Ошибка</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Вернуться на главную
                    </button>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Обработка данных...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                        <div className="flex-1">
                            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2">
                                {data.workout.name}
                            </h1>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                    <User size={16} />
                                    <span>Клиент: {data.client.name}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Calendar size={16} />
                                    <span>{formatDate(data.workout.date)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="text-left sm:text-right">
                            <p className="text-sm text-gray-500">Тренер</p>
                            <p className="font-medium text-gray-900">{data.trainer.name}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
                {data.workout.description && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                        <p className="text-blue-800 text-sm sm:text-base">{data.workout.description}</p>
                    </div>
                )}

                {/* Exercises */}
                <div className="space-y-3 sm:space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                            Упражнения ({data.exercises.length})
                        </h2>
                        {data.total_exercises > 0 && (
                            <div className="flex items-center gap-3 w-full sm:w-auto">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <span>Прогресс:</span>
                                    <span className="font-medium">{data.completed_exercises}/{data.total_exercises}</span>
                                </div>
                                <div className="flex-1 sm:w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-green-500 transition-all duration-300"
                                        style={{ 
                                            width: `${Math.round((data.completed_exercises / data.total_exercises) * 100)}%` 
                                        }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {data.exercises.map((exercise, index) => (
                        <div key={index} className={`rounded-lg shadow-sm border p-4 sm:p-6 transition-all duration-200 ${
                            exercise.is_completed 
                                ? 'border-green-300 bg-green-50' 
                                : 'border-orange-300 bg-orange-50'
                        }`}>
                            <div className="flex items-start justify-between mb-3 sm:mb-4">
                                <div className="flex-1">
                                    <h3 className={`text-base sm:text-lg font-semibold mb-2 ${
                                        exercise.is_completed ? 'text-green-800' : 'text-gray-900'
                                    }`}>
                                        {index + 1}. {exercise.name}
                                    </h3>
                                    {exercise.notes && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 sm:p-3 mb-2 sm:mb-3">
                                            <p className="text-yellow-800 text-sm">{exercise.notes}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-center w-8 h-8 ml-2">
                                    <button
                                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                        title={`Подробности упражнения: ${exercise.name}`}
                                        onClick={() => handleShowExerciseDetails(exercise)}
                                    >
                                        <HelpCircle size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm ${
                                    exercise.is_completed ? 'text-green-600' : 'text-orange-600'
                                }">
                                    <div className="flex items-center">
                                        <Layers size={16} className={`mr-1 ${
                                            exercise.is_completed ? 'text-green-500' : 'text-orange-500'
                                        }`} />
                                        <span>{exercise.sets}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Repeat size={16} className={`mr-1 ${
                                            exercise.is_completed ? 'text-green-500' : 'text-orange-500'
                                        }`} />
                                        <span>{exercise.reps}</span>
                                    </div>
                                    {exercise.weight && (
                                        <div className="flex items-center">
                                            <Weight size={16} className={`mr-1 ${
                                                exercise.is_completed ? 'text-green-500' : 'text-orange-500'
                                            }`} />
                                            <span>{exercise.weight} кг</span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Кнопка выполнения */}
                                <button
                                    onClick={() => {
                                        console.log('Клик по кнопке выполнения упражнения:', index);
                                        console.log('Упражнение:', exercise);
                                        // Отправляем workout_exercise_id вместо индекса
                                        handleCompleteExercise(exercise.workout_exercise_id || index);
                                    }}
                                    disabled={completingExercise === index}
                                    className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
                                        exercise.is_completed
                                            ? 'bg-green-500 hover:bg-green-600 text-white'
                                            : 'bg-orange-200 hover:bg-orange-300 text-orange-600'
                                    } ${completingExercise === index ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title={exercise.is_completed ? 'Отменить выполнение' : 'Отметить как выполненное'}
                                >
                                    {completingExercise === index ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                    ) : (
                                        <CheckCircle size={16} />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                    <div className="text-center text-xs sm:text-sm text-gray-500">
                        <p>Ссылка действительна до: {formatDate(data.expires_at, data.trainer.timezone)}</p>
                        <p className="mt-1">Создано тренером {data.trainer.name}</p>
                    </div>
                </div>
            </div>

            {/* Модальное окно с подробностями упражнения */}
            {showExerciseModal && selectedExercise && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Подробности упражнения
                            </h3>
                            <button
                                onClick={() => setShowExerciseModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <div>
                                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                                    {selectedExercise.name}
                                </h4>
                                {selectedExercise.description && (
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {selectedExercise.description}
                                    </p>
                                )}
                            </div>

                            {/* Медиафайлы */}
                            {(selectedExercise.image_url || selectedExercise.video_url) && (
                                <div className="space-y-3">
                                    {selectedExercise.image_url && (
                                        <div>
                                            <h5 className="text-sm font-medium text-gray-700 mb-2">Демонстрационное фото:</h5>
                                            <img 
                                                src={selectedExercise.image_url} 
                                                alt={`Демонстрация упражнения ${selectedExercise.name}`}
                                                className="w-full h-48 object-cover rounded-lg border border-gray-200"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                            />
                                            <div className="hidden text-sm text-gray-500 text-center py-2">
                                                Изображение недоступно
                                            </div>
                                        </div>
                                    )}
                                    
                                    {selectedExercise.video_url && (
                                        <div>
                                            <h5 className="text-sm font-medium text-gray-700 mb-2">Демонстрационное видео:</h5>
                                            <video 
                                                controls
                                                className="w-full h-48 object-cover rounded-lg border border-gray-200"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                            >
                                                <source src={selectedExercise.video_url} type="video/mp4" />
                                                <source src={selectedExercise.video_url} type="video/webm" />
                                                Ваш браузер не поддерживает видео.
                                            </video>
                                            <div className="hidden text-sm text-gray-500 text-center py-2">
                                                Видео недоступно
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Инструкции по выполнению */}
                            {selectedExercise.instructions && (
                                <div>
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Инструкции по выполнению:</h5>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <p className="text-sm text-blue-800 leading-relaxed whitespace-pre-line">
                                            {selectedExercise.instructions}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Время отдыха (если есть) */}
                            {selectedExercise.rest_seconds && (
                                <div className="flex items-center">
                                    <Clock size={16} className="text-gray-500 mr-2" />
                                    <span className="text-sm text-gray-600">Отдых между подходами: <strong>{formatTime(selectedExercise.rest_seconds)}</strong></span>
                                </div>
                            )}

                            {selectedExercise.notes && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <h5 className="text-sm font-medium text-yellow-800 mb-2">Заметки тренера:</h5>
                                    <p className="text-sm text-yellow-700">{selectedExercise.notes}</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button
                                onClick={() => setShowExerciseModal(false)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SharedExercises; 